import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { supabase, PHOTOS_BUCKET } from "@/lib/supabase";
import { DEFAULT_PRICING, mergePricing } from "@/data/defaultPricing";

// Les tarifs sont stockés dans un fichier JSON du bucket "photos" (déjà existant).
// Pas besoin de table ni de permission supplémentaire : lecture publique, écriture admin.
const CONFIG_PATH = "config/pricing.json";

const PricingContext = createContext(null);

export function PricingProvider({ children }) {
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const { data } = supabase.storage.from(PHOTOS_BUCKET).getPublicUrl(CONFIG_PATH);
      // cache-bust pour toujours récupérer la dernière version
      const res = await fetch(`${data.publicUrl}?t=${Date.now()}`);
      if (res.ok) {
        const json = await res.json();
        setPricing(mergePricing(DEFAULT_PRICING, json));
      } else {
        setPricing(DEFAULT_PRICING);
      }
    } catch {
      setPricing(DEFAULT_PRICING);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const value = useMemo(() => ({ pricing, loading, reload }), [pricing, loading, reload]);
  return <PricingContext.Provider value={value}>{children}</PricingContext.Provider>;
}

export const usePricing = () => {
  const ctx = useContext(PricingContext);
  if (!ctx) throw new Error("usePricing must be used within PricingProvider");
  return ctx;
};

// Sauvegarde des tarifs (admin connecté uniquement, via la policy d'écriture du bucket)
export async function savePricing(pricingObject) {
  const blob = new Blob([JSON.stringify(pricingObject)], { type: "application/json" });
  const { error } = await supabase.storage.from(PHOTOS_BUCKET).upload(CONFIG_PATH, blob, {
    upsert: true,
    contentType: "application/json",
    cacheControl: "60",
  });
  return error;
}
